import { useEffect, useMemo, useState } from "react";
import { fetchSheetViaProxy } from "../../lib/sheets";
import RaceSessionSelect from "../../components/RaceSessionSelect";
import { getAutoRaceFromSchedule } from "../../lib/autoRaceFromSchedule";

const SHEET_ID = "1bdWoKQm-B96F6fGmeB7Lo0BAiKDglXsQboXaVdtkIZI";
const SCHEDULE_RANGE = "Schedule!A1:D";
const SHOW_RACE_TEAM_COLUMNS = false;
const SHOW_RACE_PEN_COLUMN = false;

// ---------- Helpers ----------
function colIndexFromLetters(letters) {
    let n = 0;
    for (let i = 0; i < letters.length; i++) n = n * 26 + (letters.charCodeAt(i) - 64);
    return n;
}
function letterToIndex(letter) {
    const num = colIndexFromLetters(letter);
    return num - 1;
}
function colLettersFromIndex(n1) {
    let n = n1, out = "";
    while (n > 0) {
        const rem = (n - 1) % 26;
        out = String.fromCharCode(65 + rem) + out;
        n = Math.floor((n - 1) / 26);
    }
    return out;
}
function lettersBetween(start, end) {
    const s = colIndexFromLetters(start);
    const e = colIndexFromLetters(end);
    const list = [];
    for (let i = s; i <= e; i++) list.push(colLettersFromIndex(i));
    return list;
}
function safeVal(row, idx) {
    const v = row?.[idx];
    return v === null || v === undefined ? "" : String(v).trim();
}

// Extrahiert eine vorzeichenbehaftete Zahl aus Strings wie "▲2", "+3","-1 ", "2"
function parseSignedIntLike(v) {
    if (v === null || v === undefined) return null;
    const s = String(v).trim();
    const m = s.match(/[+-]?\d+/);
    if (!m) return null;
    const n = parseInt(m[0], 10);
    return Number.isNaN(n) ? null : n;
}

const imgFlag = (src) => <img src={src} alt="Flag" className="flag" loading="lazy" />;
const imgCar = (src) => <img src={src} alt="Car" className="carlogo" loading="lazy" />;

const isNumeric = (v) => v !== null && v !== "" && !isNaN(Number(v));

function isSheetMissingResponse(res) {
    if (!res || typeof res !== "object") return true;
    const hasHeader = Array.isArray(res.header) && res.header.length > 0;
    const hasRows = Array.isArray(res.rows) && res.rows.length > 0;
    return !hasHeader && !hasRows;
}

function parseLapTimeToSeconds(v) {
    const s = String(v ?? "").trim();
    if (!s) return null;

    const up = s.toUpperCase();
    if (up === "DNS" || up === "DNF" || up === "BAN") return null;

    const m = s.match(/^(\d+):(\d{2})(?:\.(\d{1,3}))?$/);
    if (m) {
        const min = Number(m[1]);
        const sec = Number(m[2]);
        const ms = m[3] ? Number(m[3].padEnd(3, "0")) : 0;
        return min * 60 + sec + ms / 1000;
    }

    const f = Number(s.replace(",", "."));
    return Number.isFinite(f) ? f : null;
}

// ---------- View-Builder ----------
function buildRaceView(header, rows) {
    const hIndex = (L) => letterToIndex(L);

    let visCols = lettersBetween("A", "N");

    // Team in RACE ausblenden:
    // H = Team-Bild-Spalte
    // I = Teamname
    if (!SHOW_RACE_TEAM_COLUMNS) {
        visCols = visCols.filter((L) => L !== "H" && L !== "I");
    }

    // Pen in RACE ausblenden:
    // L = Pen
    if (!SHOW_RACE_PEN_COLUMN) {
        visCols = visCols.filter((L) => L !== "L");
    }

    const baseIdx = visCols.map((L) => hIndex(L));

    const idxFlagURL = hIndex("S");
    const idxCarURL = hIndex("T");

    const idxCarNo = hIndex("E");
    const idxDriver = hIndex("G");

    const posOf = (letter) => visCols.indexOf(letter);

    const toD = posOf("D"); // Flag
    const toE = posOf("E"); // #
    const toF = posOf("F"); // Auto
    const toG = posOf("G"); // Fahrer

    const viewHeader = visCols.map((L) => header[hIndex(L)] ?? L);

    if (toE >= 0) {
        viewHeader[toE] = "#";
    }

    const startIdx = posOf("B");
    if (startIdx >= 0 && (viewHeader[startIdx] || "").toLowerCase() === "start pos") {
        viewHeader[startIdx] = "Start";
    }

    const viewRows = rows.map((r) => {
        const row = baseIdx.map((ci) => r[ci]);

        const flagSrc = safeVal(r, idxFlagURL);
        if (flagSrc && toD >= 0) row[toD] = imgFlag(flagSrc);

        if (toE >= 0) row[toE] = r[idxCarNo];

        const carSrc = safeVal(r, idxCarURL);
        if (carSrc && toF >= 0) row[toF] = imgCar(carSrc);

        if (toG >= 0) row[toG] = r[idxDriver];

        return row;
    });

    return { header: viewHeader, rows: viewRows };
}

function buildQualyPracticeView(header, rows) {
    const hIndex = (L) => letterToIndex(L);
    const visCols = lettersBetween("A", "G");
    const baseIdx = visCols.map((L) => hIndex(L));

    const idxFlagURL = hIndex("J");
    const idxAutoURL = hIndex("K");

    const targetB = hIndex("B");
    const targetD = hIndex("D");

    const viewHeader = visCols.map((L) => header[hIndex(L)] ?? L);

    const visibleCpos = 2;
    if ((viewHeader[visibleCpos] || "").toLowerCase().includes("fahrzeugnummer")) {
        viewHeader[visibleCpos] = "#";
    }

    const viewRows = rows.map((r) => {
        const row = baseIdx.map((ci) => r[ci]);

        const flagSrc = safeVal(r, idxFlagURL);
        if (flagSrc) row[targetB - hIndex("A")] = imgFlag(flagSrc);

        const carSrc = safeVal(r, idxAutoURL);
        if (carSrc) row[targetD - hIndex("A")] = imgCar(carSrc);

        return row;
    });

    return { header: viewHeader, rows: viewRows };
}

// ---------- Nur Single-Class-Konfiguration ----------
const cfg = {
    RACE: {
        fetchRange: (r) => `R${r}_RACE!A1:U100`,
        buildView: buildRaceView,
    },
    QUALY: {
        fetchRange: (r) => `R${r}_QUALY!A1:K100`,
        buildView: buildQualyPracticeView,
    },
    PRACTICE: {
        fetchRange: (r) => `R${r}_PRACTICE!A1:K100`,
        buildView: buildQualyPracticeView,
    },
};

export default function ResultsSpecialX() {
    const [race, setRace] = useState(null);
    const [raceTouched, setRaceTouched] = useState(false);
    const [trackLabel, setTrackLabel] = useState("");

    const [session, setSession] = useState("RACE");

    const [viewHeader, setViewHeader] = useState([]);
    const [viewRows, setViewRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const auto = await getAutoRaceFromSchedule(
                    fetchSheetViaProxy,
                    SCHEDULE_RANGE,
                    "results",
                    SHEET_ID
                );
                if (!raceTouched) setRace(auto);
            } catch (e) {
                console.warn("Schedule auto-race failed (results)", e);
                if (!raceTouched) setRace(1);
            }
        })();
    }, [raceTouched]);

    useEffect(() => {
        (async () => {
            if (!race) return;

            setLoading(true);
            setMsg("");
            setViewHeader([]);
            setViewRows([]);

            try {
                const sessionCfg = cfg[session];
                if (!sessionCfg) {
                    setMsg("Ungültige Session-Auswahl.");
                    return;
                }

                const { fetchRange, buildView } = sessionCfg;

                const res = await fetchSheetViaProxy({
                    range: fetchRange(race),
                    sheetId: SHEET_ID,
                });

                if (isSheetMissingResponse(res)) {
                    setMsg("Rennen hat noch nicht stattgefunden oder wurde noch nicht ausgewertet.");
                    return;
                }

                const header = Array.isArray(res?.header) ? res.header : [];
                const rows = Array.isArray(res?.rows)
                    ? res.rows.map((r) => header.map((h) => r[h]))
                    : [];

                const { header: vHeader, rows: vRows } = buildView(header, rows);
                setViewHeader(vHeader);
                setViewRows(vRows);
            } catch (e) {
                console.warn("Results load failed", e);
                setMsg("Rennen hat noch nicht stattgefunden oder wurde noch nicht ausgewertet.");
            } finally {
                setLoading(false);
            }
        })();
    }, [race, session]);

    const colIdxMap = useMemo(() => {
        const map = new Map();
        viewHeader.forEach((h, i) => map.set((h || "").toLowerCase(), i));
        return map;
    }, [viewHeader]);

    const deltaCol = useMemo(() => {
        if (session === "RACE") return 2;
        return colIdxMap.get("delta pos");
    }, [session, colIdxMap]);

    const numericCols = useMemo(() => {
        const set = new Set();
        viewRows.forEach((row) =>
            row.forEach((cell, i) => {
                if (isNumeric(cell)) set.add(i);
            })
        );
        return set;
    }, [viewRows]);

    const fastestLapCol = useMemo(() => {
        const idx = viewHeader.findIndex((h) =>
            String(h ?? "").toLowerCase().includes("fastest")
        );
        return idx >= 0 ? idx : null;
    }, [viewHeader]);

    const fastestLapBest = useMemo(() => {
        if (fastestLapCol === null) return null;

        let best = null;
        for (const row of viewRows) {
            const t = parseLapTimeToSeconds(row?.[fastestLapCol]);
            if (t === null) continue;
            if (best === null || t < best) best = t;
        }
        return best;
    }, [viewRows, fastestLapCol]);

    const avgLapCol = useMemo(() => {
        const idx = viewHeader.findIndex((h) => {
            const s = String(h ?? "").toLowerCase();
            return s.includes("avg lap") || s.includes("average lap");
        });
        return idx >= 0 ? idx : null;
    }, [viewHeader]);

    const bestLapCol = useMemo(() => {
        const idx = viewHeader.findIndex((h) => {
            const s = String(h ?? "").toLowerCase();
            return s.includes("best lap");
        });
        return idx >= 0 ? idx : null;
    }, [viewHeader]);

    return (
        <>
            <RaceSessionSelect
                valueRace={race}
                onRaceChange={setRace}
                onUserRaceChange={() => setRaceTouched(true)}
                autoSelectFirst={false}
                valueSession={session}
                onSessionChange={setSession}
                showSession={true}
                showClass={false}
                onTrackLabelChange={setTrackLabel}
                sheetId={SHEET_ID}
                scheduleRange={SCHEDULE_RANGE}
                raceLabelPrefix="R"
            />

            <div className="card overview-card-top">
                <h1>Ergebnisse</h1>
                <p className="muted results-subtitle">
                    {session} <span className="pill pill--gesamt">Gesamt</span>
                    {trackLabel ? <> · {trackLabel}</> : null}
                </p>
            </div>

            {msg && (
                <div className="card">
                    <p className="text-dim">{msg}</p>
                </div>
            )}

            {loading && (
                <div className="card">
                    <p>Lade…</p>
                </div>
            )}

            {!loading && !msg && (
                <div className="fancyTable">
                    <table className="tbl">
                        <thead>
                        <tr>
                            {viewHeader.map((h, i) => {
                                const isAvg = avgLapCol !== null && i === avgLapCol;
                                const isBest = bestLapCol !== null && i === bestLapCol;

                                return (
                                    <th key={i} className={(isAvg || isBest) ? "lap-center" : ""}>
                                        {h}
                                    </th>
                                );
                            })}
                        </tr>
                        </thead>

                        <tbody>
                        {viewRows.map((r, ri) => (
                            <tr key={ri}>
                                {r.map((cell, ci) => {
                                    let cls = "";

                                    if (numericCols.has(ci)) cls = "num";

                                    if (typeof deltaCol === "number" && ci === deltaCol) {
                                        const n = parseSignedIntLike(cell);
                                        if (n !== null) {
                                            if (n > 0) cls = `${cls} delta-up`.trim();
                                            else if (n < 0) cls = `${cls} delta-down`.trim();
                                        }
                                    }

                                    const headerName = (viewHeader?.[ci] || "").toLowerCase();
                                    if (headerName.includes("pen") && String(cell).trim() !== "") {
                                        cls = `${cls} penalty-red`.trim();
                                    }

                                    if (fastestLapCol !== null && fastestLapBest !== null && ci === fastestLapCol) {
                                        const t = parseLapTimeToSeconds(cell);
                                        if (t !== null && Math.abs(t - fastestLapBest) < 0.0005) {
                                            cls = `${cls} fastest-best`.trim();
                                        }
                                    }

                                    if (avgLapCol !== null && ci === avgLapCol) {
                                        cls = `${cls} lap-center`.trim();
                                    }
                                    if (bestLapCol !== null && ci === bestLapCol) {
                                        cls = `${cls} lap-center`.trim();
                                    }

                                    return (
                                        <td key={ci} className={cls}>
                                            {cell}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
}